import React, { useState, useEffect } from 'react';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  VideoTrack,
  useRemoteParticipants,
  useLocalParticipant,
  useTracks
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import api from '../../../utils/api';
import { config } from '../../../config';
import { useAuth } from '../../../contexts/AuthContext';
import { Heading } from '../../ui/heading';

interface BriefingProps {
  className?: string;
}

interface BriefingTokenResponse {
  room_name: string;
  user_token: string;
}

const JunoBriefing: React.FC<BriefingProps> = ({ className = '' }) => {
  const [token, setToken] = useState<string>('');
  const [roomName, setRoomName] = useState<string>('');
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [briefingStarted, setBriefingStarted] = useState(false);
  
  const { user } = useAuth();

  const startBriefing = async () => {
    if (isLoading || briefingStarted) {
      return;
    }

    if (!user || !user.organization_id) {
      setError("No organization context found. Please ensure you are logged in correctly.");
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      const response = await api.getApi().post('/v1/livekit/briefing/token', {
        organization_id: user.organization_id,
        briefing_type: 'morning'
      });

      const data: BriefingTokenResponse = response.data;
      
      setToken(data.user_token);
      setRoomName(data.room_name);
      setBriefingStarted(true);
      
    } catch (err) {
      console.error('Error starting briefing:', err);
      setError(err instanceof Error ? err.message : 'Failed to start briefing');
    } finally {
      setIsLoading(false);
    }
  };

  const endBriefing = () => {
    setBriefingStarted(false);
    setIsConnected(false);
    setToken('');
    setRoomName('');
    setError('');
  };

  if (!briefingStarted) {
    return (
      <div className={`h-full bg-white flex items-center justify-center ${className}`}>
        <div className="max-w-2xl mx-auto text-center p-8">
          <div className="mb-12">
            <Heading level={1} weight="bold" className="text-4xl text-gray-900 mb-4">
              Good morning
            </Heading>
          </div>
          
          <div className="space-y-6">
            {/* Glowing Juno Orb Button */}
            <div className="flex justify-center">
              <button
                onClick={startBriefing}
                disabled={isLoading}
                className="relative group"
                aria-label="Start Intelligence Briefing"
              >
                {/* Main Orb */}
                <div className={`relative w-20 h-20 rounded-full transition-all duration-300 ${
                  isLoading ? 'animate-pulse' : 'group-hover:scale-110'
                }`}>
                  {/* Outer Glow */}
                  <div className={`absolute inset-0 rounded-full blur-lg opacity-50 ${
                    isLoading ? 'bg-yellow-400' : 'bg-blue-500 group-hover:bg-blue-400'
                  } animate-pulse`}></div>
                  
                  {/* Middle Glow */}
                  <div className={`absolute inset-1 rounded-full blur-md opacity-60 ${
                    isLoading ? 'bg-yellow-300' : 'bg-blue-400 group-hover:bg-blue-300'
                  }`}></div>
                  
                  {/* Inner Orb */}
                  <div className={`relative w-full h-full rounded-full ${
                    isLoading 
                      ? 'bg-gradient-to-br from-yellow-300 to-orange-400' 
                      : 'bg-gradient-to-br from-blue-400 to-blue-600 group-hover:from-blue-300 group-hover:to-blue-500'
                  } flex items-center justify-center shadow-xl transition-all duration-300`}>
                    
                    {/* Shimmer Effect */}
                    <div className="absolute inset-0 rounded-full overflow-hidden">
                      <div className={`absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 ${
                        isLoading ? '' : 'animate-shimmer'
                      }`}></div>
                    </div>
                    
                    {/* Icon or Spinner */}
                    {isLoading ? (
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                    )}
                  </div>
                </div>
                
                {/* Label */}
                <div className="mt-4">
                  <span className="text-lg font-medium text-gray-700">
                    {isLoading ? 'Connecting to Juno...' : 'Start'}
                  </span>
                </div>
              </button>
            </div>
            
            {error && (
              <div className="text-red-600 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full bg-white ${className}`}>
      {token && roomName && (
        <LiveKitRoom
          video={true}
          audio={true}
          connect={true}
          token={token}
          serverUrl={config.LIVEKIT_URL}
          data-lk-theme="default"
          className="h-full"
          onConnected={() => {
            setIsConnected(true);
          }}
          onDisconnected={() => {
            setIsConnected(false);
          }}
          onError={(error: Error) => {
            console.error('Room error:', error);
            setError(error.message);
          }}
        >
          <div className="h-full flex flex-col">
            {/* Clean minimal header */}
            <div className="bg-white border-b border-gray-200 p-4">
              <div className="flex items-center justify-end">
                <button
                  onClick={endBriefing}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  End Briefing
                </button>
              </div>
            </div>

            {/* Main briefing area */}
            <div className="flex-1">
              {isConnected ? (
                <BriefingVideoLayout />
              ) : (
                <div className="h-full flex items-center justify-center bg-white">
                  <div className="w-8 h-8 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
          </div>
          
          <RoomAudioRenderer />
        </LiveKitRoom>
      )}
    </div>
  );
};

// Clean briefing video layout with Juno as main video and user as small tile
const BriefingVideoLayout: React.FC = () => {
  const { localParticipant } = useLocalParticipant();
  const remoteParticipants = useRemoteParticipants();
  
  // Get video tracks for display
  const remoteTracks = useTracks([Track.Source.Camera], {
    onlySubscribed: true,
  });

  const localTracks = useTracks([Track.Source.Camera], {
    participant: localParticipant,
    onlySubscribed: false,
  });

  // Find the Juno agent participant
  const junoParticipant = remoteParticipants.find((p: any) => 
    p.identity === 'Juno Intelligence Briefing' || 
    p.name === 'Juno Intelligence Briefing' ||
    p.identity?.includes('agent')
  );

  const junoVideoTrack = remoteTracks.find((track: any) => 
    track.participant === junoParticipant
  );

  const localVideoTrack = localTracks[0];

  return (
    <div className="w-full h-full relative bg-white">
      {/* Main Juno video - full screen with clean rounded corners */}
      <div className="w-full h-full p-6">
        {junoVideoTrack ? (
          <div className="w-full h-full rounded-3xl overflow-hidden shadow-lg">
            <VideoTrack 
              trackRef={junoVideoTrack}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="w-full h-full rounded-3xl bg-gray-100 flex items-center justify-center shadow-lg">
            <div className="w-8 h-8 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
      </div>

      {/* Small user video - bottom right corner with clean design */}
      <div className="absolute bottom-6 right-6 w-64 h-48 rounded-2xl overflow-hidden shadow-lg">
        {localVideoTrack ? (
          <VideoTrack 
            trackRef={localVideoTrack}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
            </svg>
          </div>
        )}
        
        {/* Subtle controls overlay */}
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2">
          <BriefingControls />
        </div>
      </div>
    </div>
  );
};

// Subtle briefing controls component
const BriefingControls: React.FC = () => {
  const { localParticipant } = useLocalParticipant();
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isCameraMuted, setIsCameraMuted] = useState(false);

  const toggleMicrophone = async () => {
    if (localParticipant) {
      const enabled = localParticipant.isMicrophoneEnabled;
      await localParticipant.setMicrophoneEnabled(!enabled);
      setIsMicMuted(enabled);
    }
  };

  const toggleCamera = async () => {
    if (localParticipant) {
      const enabled = localParticipant.isCameraEnabled;
      await localParticipant.setCameraEnabled(!enabled);
      setIsCameraMuted(enabled);
    }
  };

  useEffect(() => {
    if (localParticipant) {
      setIsMicMuted(!localParticipant.isMicrophoneEnabled);
      setIsCameraMuted(!localParticipant.isCameraEnabled);
    }
  }, [localParticipant?.isMicrophoneEnabled, localParticipant?.isCameraEnabled]);

  return (
    <div className="flex space-x-2">
      <button 
        onClick={toggleMicrophone}
        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
          isMicMuted ? 'bg-red-500 text-white' : 'bg-white/90 text-gray-600 hover:bg-white'
        } shadow-sm`}
        title={isMicMuted ? 'Unmute microphone' : 'Mute microphone'}
      >
        {isMicMuted ? (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.745 3.745 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.745 3.745 0 013.296-1.043A3.745 3.745 0 0112 3a3.745 3.745 0 013.068 1.593 3.745 3.745 0 013.296 1.043 3.745 3.745 0 011.043 3.296A3.745 3.745 0 0121 12z"/>
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"/>
          </svg>
        )}
      </button>
      
      <button 
        onClick={toggleCamera}
        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
          isCameraMuted ? 'bg-red-500 text-white' : 'bg-white/90 text-gray-600 hover:bg-white'
        } shadow-sm`}
        title={isCameraMuted ? 'Turn on camera' : 'Turn off camera'}
      >
        {isCameraMuted ? (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
          </svg>
        )}
      </button>
    </div>
  );
};

export default JunoBriefing;